import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { DatabaseOperationError, executeDatabaseOperation } from '@/db/errors'
import { summarizeInspectionResults } from '@/db/rules'
import { inspectionResultSchema, inspectionSchema } from '@/db/schemas'
import type { InspectionResult, InspectionResultStatus } from '@/db/types'
import { requireRecord, writeAuditLog } from './helpers'

export class InspectionResultRepository {
  constructor(private readonly database: RealMachinesDatabase = getDatabase()) {}

  async listByInspection(inspectionId: string): Promise<InspectionResult[]> {
    return executeDatabaseOperation('listar resultados da inspeção', async () => {
      const results = await this.database.inspectionResults
        .where('inspectionId')
        .equals(inspectionId)
        .sortBy('itemOrder')
      return results.map((result) => inspectionResultSchema.parse(result))
    })
  }

  async update(
    id: string,
    input: { status?: InspectionResultStatus; observation?: string },
  ): Promise<InspectionResult> {
    return executeDatabaseOperation('editar resultado da inspeção', async () =>
      this.database.transaction(
        'rw',
        [this.database.inspectionResults, this.database.inspections, this.database.auditLogs],
        async () => {
          if (!input.status && input.observation === undefined) {
            throw new DatabaseOperationError('validation', 'Nenhuma alteração foi informada.')
          }
          const existing = inspectionResultSchema.parse(
            requireRecord(await this.database.inspectionResults.get(id), 'Resultado'),
          )
          const inspection = inspectionSchema.parse(
            requireRecord(await this.database.inspections.get(existing.inspectionId), 'Inspeção'),
          )
          if (inspection.status !== 'draft') {
            throw new DatabaseOperationError(
              'invalid_state',
              'Resultados só podem ser alterados em rascunho.',
            )
          }
          const now = new Date().toISOString()
          const result = inspectionResultSchema.parse({
            ...existing,
            ...(input.status ? { status: input.status } : {}),
            ...(input.observation !== undefined ? { observation: input.observation.trim() } : {}),
            updatedAt: now,
          })
          await this.database.inspectionResults.put(result)
          const allResults = await this.database.inspectionResults
            .where('inspectionId')
            .equals(existing.inspectionId)
            .toArray()
          const priority = summarizeInspectionResults(allResults).priority
          await this.database.inspections.update(inspection.id, { priority, updatedAt: now })
          await writeAuditLog(
            this.database,
            'inspectionResult',
            id,
            'update',
            { fields: Object.keys(input).sort() },
            now,
          )
          return result
        },
      ),
    )
  }
}
